import React, { useEffect, useState } from "react";
import { shuffleArray } from "src/handler/Helper";
import { GetCoursesForExam } from "src/handler/coursehandler";

interface Course {
    courseId: number;
    courseName: string;
    serverName: string;
    examEligible: boolean;
    attemptCount: number;
    attemptTime: string;
    isPassed: boolean;
}

interface CourseTest {
    courseTestId: number;
    courseId: number;
    courseQuestion: string;
    option1: string;
    option2: string;
    option3: string;
    option4: string;
    answerOption: number;
}

const PASSING_PERCENTAGE = 70;
const QUESTION_TIME = 15;

const CourseTestSlider: React.FC = () => {
    const [courses, setCourses] = useState<Course[]>([]);
    const [selectedCourse, setSelectedCourse] =
        useState<Course>();

    const [selectedCourseId, setSelectedCourseId] =
        useState<number | null>(null);

    const [questions, setQuestions] =
        useState<CourseTest[]>([]);

    const [currentIndex, setCurrentIndex] =
        useState(0);

    const [answers, setAnswers] =
        useState<Record<number, number>>({});

    const [timer, setTimer] =
        useState(QUESTION_TIME);

    const [submitted, setSubmitted] =
        useState(false);

    const [score, setScore] =
        useState(0);

    const [startExam, setStartExam] =
        useState(false);

    const [loading, setLoading] =
        useState(false);

    const [transitioning, setTransitioning] =
        useState(false);

    /* =========================================================
       THEME
    ========================================================= */

    const theme = {
        bg: "#070b12",
        bgSecondary: "#0b111b",
        panel: "rgba(15, 23, 36, 0.88)",
        panelLight: "rgba(22, 32, 48, 0.72)",

        border: "rgba(148, 163, 184, 0.13)",
        borderStrong: "rgba(96, 165, 250, 0.30)",

        primary: "#60a5fa",
        primaryDark: "#2563eb",

        text: "#f1f5f9",
        textSecondary: "#94a3b8",
        textMuted: "#64748b",

        success: "#67e8f9",
        danger: "#fb7185",

        shadow:
            "0 25px 80px rgba(0, 0, 0, .40)",
    };

    /* =========================================================
       COMMON STYLES
    ========================================================= */

    const styles = {
        page: {
            minHeight: "100vh",
            background: `
                radial-gradient(
                    circle at 15% 10%,
                    rgba(37, 99, 235, .12),
                    transparent 30%
                ),
                radial-gradient(
                    circle at 85% 15%,
                    rgba(14, 165, 233, .08),
                    transparent 28%
                ),
                radial-gradient(
                    circle at 50% 100%,
                    rgba(30, 64, 175, .08),
                    transparent 35%
                ),
                ${theme.bg}
            `,
            color: theme.text,
            fontFamily:
                "'Inter', 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
            position: "relative" as const,
            overflow: "hidden" as const,
        },

        grid: {
            position: "fixed" as const,
            inset: 0,
            pointerEvents: "none" as const,

            backgroundImage: `
                linear-gradient(
                    rgba(148,163,184,.025) 1px,
                    transparent 1px
                ),
                linear-gradient(
                    90deg,
                    rgba(148,163,184,.025) 1px,
                    transparent 1px
                )
            `,

            backgroundSize: "45px 45px",
            maskImage:
                "linear-gradient(to bottom, black, transparent)",
        },

        selectionWrapper: {
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "30px 20px",
            position: "relative" as const,
            zIndex: 2,
            boxSizing: "border-box" as const,
        },

        selectionCard: {
            width: "100%",
            maxWidth: "680px",

            background:
                "linear-gradient(145deg, rgba(17,25,39,.94), rgba(8,13,22,.97))",

            border:
                `1px solid ${theme.border}`,

            borderRadius: "22px",

            boxShadow: theme.shadow,

            backdropFilter: "blur(20px)",

            overflow: "hidden",
        },

        selectionHeader: {
            padding: "30px",

            borderBottom:
                "1px solid rgba(148,163,184,.10)",

            background:
                "linear-gradient(135deg, rgba(37,99,235,.10), rgba(14,165,233,.025))",
        },

        securityLine: {
            display: "flex",
            alignItems: "center",
            gap: "8px",
            marginBottom: "18px",
        },

        securityDot: {
            width: "8px",
            height: "8px",
            borderRadius: "50%",
            background: "#60a5fa",
            boxShadow:
                "0 0 12px rgba(96,165,250,.8)",
        },

        securityText: {
            fontSize: "10px",
            fontWeight: 700,
            letterSpacing: "1.5px",
            color: "#93c5fd",
            textTransform: "uppercase" as const,
        },

        selectionTitle: {
            margin: 0,
            fontSize: "27px",
            fontWeight: 750,
            letterSpacing: "-.5px",
            color: "#f8fafc",
        },

        selectionSubtitle: {
            margin: "9px 0 0",
            fontSize: "13px",
            lineHeight: 1.7,
            color: theme.textSecondary,
        },

        selectionBody: {
            padding: "30px",
        },

        label: {
            display: "block",
            fontSize: "11px",
            fontWeight: 700,
            color: "#94a3b8",
            letterSpacing: ".8px",
            textTransform: "uppercase" as const,
            marginBottom: "9px",
        },

        select: {
            width: "100%",
            height: "50px",

            padding: "0 15px",

            borderRadius: "10px",

            border:
                "1px solid rgba(148,163,184,.18)",

            background: "#0b1220",

            color: "#e2e8f0",

            fontSize: "14px",

            outline: "none",

            cursor: "pointer",

            boxSizing: "border-box" as const,
        },

        infoPanel: {
            marginTop: "20px",

            padding: "18px",

            borderRadius: "12px",

            background:
                "rgba(96,165,250,.035)",

            border:
                "1px solid rgba(96,165,250,.10)",
        },

        infoTitle: {
            margin: "0 0 12px",

            fontSize: "11px",

            fontWeight: 700,

            color: "#bfdbfe",

            letterSpacing: ".8px",

            textTransform: "uppercase" as const,
        },

        infoRow: {
            display: "flex",
            justifyContent: "space-between",
            gap: "15px",
            padding: "7px 0",
            borderBottom:
                "1px solid rgba(148,163,184,.06)",
        },

        infoLabel: {
            fontSize: "12px",
            color: "#64748b",
        },

        infoValue: {
            fontSize: "12px",
            color: "#cbd5e1",
            fontWeight: 600,
            textAlign: "right" as const,
        },

        startButton: {
            width: "100%",
            height: "50px",

            marginTop: "22px",

            border: "1px solid rgba(96,165,250,.35)",

            borderRadius: "10px",

            background:
                "linear-gradient(135deg, #1d4ed8, #2563eb)",

            color: "#ffffff",

            fontSize: "13px",

            fontWeight: 700,

            letterSpacing: ".2px",

            cursor: "pointer",

            boxShadow:
                "0 10px 30px rgba(37,99,235,.20)",
        },

        /* ================= EXAM ================= */

        examWrapper: {
            minHeight: "100vh",

            padding:
                "18px 20px 40px",

            position: "relative" as const,

            zIndex: 2,

            boxSizing: "border-box" as const,
        },

        examContainer: {
            width: "100%",
            maxWidth: "1100px",
            margin: "0 auto",
        },

        examHeader: {
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "20px",

            padding: "17px 20px",

            borderRadius: "15px",

            background:
                "linear-gradient(135deg, rgba(17,25,39,.96), rgba(10,16,27,.96))",

            border:
                `1px solid ${theme.border}`,

            boxShadow:
                "0 15px 45px rgba(0,0,0,.25)",

            backdropFilter: "blur(18px)",
        },

        examBrand: {
            display: "flex",
            alignItems: "center",
            gap: "13px",
            minWidth: 0,
        },

        brandIcon: {
            width: "38px",
            height: "38px",
            borderRadius: "10px",

            display: "flex",
            alignItems: "center",
            justifyContent: "center",

            background:
                "linear-gradient(135deg, #1e3a8a, #2563eb)",

            border:
                "1px solid rgba(147,197,253,.25)",

            fontSize: "17px",

            boxShadow:
                "0 0 25px rgba(37,99,235,.20)",
        },

        examName: {
            margin: 0,
            fontSize: "14px",
            fontWeight: 700,
            color: "#f8fafc",
            whiteSpace: "nowrap" as const,
            overflow: "hidden",
            textOverflow: "ellipsis",
        },

        examStatus: {
            margin: "3px 0 0",
            fontSize: "10px",
            color: "#64748b",
        },

        secureBadge: {
            display: "flex",
            alignItems: "center",
            gap: "7px",

            padding:
                "7px 10px",

            borderRadius: "8px",

            background:
                "rgba(34,211,238,.035)",

            border:
                "1px solid rgba(34,211,238,.10)",

            color: "#67e8f9",

            fontSize: "9px",

            fontWeight: 700,

            letterSpacing: ".8px",

            textTransform: "uppercase" as const,
        },

        timer: {
            minWidth: "80px",

            padding:
                "10px 12px",

            borderRadius: "9px",

            background:
                "rgba(15,23,42,.9)",

            border:
                "1px solid rgba(148,163,184,.15)",

            textAlign: "center" as const,

            fontSize: "14px",

            fontWeight: 750,

            color: "#e2e8f0",
        },

        progressPanel: {
            marginTop: "12px",

            padding:
                "13px 16px",

            borderRadius: "12px",

            background:
                "rgba(15,23,36,.80)",

            border:
                `1px solid ${theme.border}`,
        },

        progressHeader: {
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",

            marginBottom: "9px",
        },

        progressText: {
            fontSize: "10px",
            fontWeight: 700,
            color: "#64748b",
            letterSpacing: ".4px",
        },

        progressTrack: {
            height: "5px",

            borderRadius: "10px",

            background:
                "rgba(148,163,184,.09)",

            overflow: "hidden",
        },

        questionCard: {
            marginTop: "14px",

            padding:
                "34px",

            borderRadius: "18px",

            background:
                "linear-gradient(145deg, rgba(17,25,39,.97), rgba(9,15,25,.98))",

            border:
                `1px solid ${theme.border}`,

            boxShadow:
                "0 25px 80px rgba(0,0,0,.32)",

            backdropFilter:
                "blur(20px)",

            transition:
                "all .25s ease",
        },

        questionLabel: {
            display: "inline-flex",

            padding:
                "6px 9px",

            borderRadius: "6px",

            background:
                "rgba(96,165,250,.07)",

            border:
                "1px solid rgba(96,165,250,.12)",

            color: "#93c5fd",

            fontSize: "9px",

            fontWeight: 750,

            letterSpacing: "1px",

            textTransform:
                "uppercase" as const,
        },

        questionText: {
            margin:
                "20px 0 30px",

            color: "#f1f5f9",

            fontSize: "21px",

            lineHeight: 1.6,

            fontWeight: 600,

            letterSpacing: "-.2px",
        },

        options: {
            display: "grid",

            gridTemplateColumns:
                "repeat(2, minmax(0, 1fr))",

            gap: "12px",
        },

        option: {
            display: "flex",

            alignItems: "flex-start",

            gap: "12px",

            minHeight: "68px",

            padding: "16px",

            borderRadius: "11px",

            border:
                "1px solid rgba(148,163,184,.13)",

            background:
                "rgba(15,23,36,.72)",

            cursor: "pointer",

            boxSizing: "border-box" as const,

            transition:
                "all .18s ease",
        },

        selectedOption: {
            border:
                "1px solid rgba(96,165,250,.48)",

            background:
                "linear-gradient(135deg, rgba(37,99,235,.14), rgba(30,64,175,.06))",

            boxShadow:
                "0 0 25px rgba(37,99,235,.07)",
        },

        radio: {
            width: "16px",
            height: "16px",

            marginTop: "2px",

            flexShrink: 0,

            accentColor: "#60a5fa",

            cursor: "pointer",
        },

        optionText: {
            fontSize: "13px",

            lineHeight: 1.55,

            color: "#cbd5e1",

            fontWeight: 500,
        },

        nextButton: {
            width: "100%",

            height: "49px",

            marginTop: "26px",

            border:
                "1px solid rgba(96,165,250,.28)",

            borderRadius: "9px",

            background:
                "linear-gradient(135deg, #1e3a8a, #2563eb)",

            color: "#ffffff",

            fontSize: "13px",

            fontWeight: 700,

            cursor: "pointer",

            boxShadow:
                "0 10px 30px rgba(37,99,235,.15)",
        },

        examFooter: {
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",

            marginTop: "13px",

            padding:
                "0 4px",

            fontSize: "9px",

            color: "#475569",

            letterSpacing: ".3px",
        },

        /* ================= RESULT ================= */

        resultWrapper: {
            minHeight: "100vh",

            display: "flex",

            alignItems: "center",

            justifyContent: "center",

            padding: "30px 20px",

            position: "relative" as const,

            zIndex: 2,

            boxSizing: "border-box" as const,
        },

        resultCard: {
            width: "100%",

            maxWidth: "540px",

            padding: "40px",

            textAlign: "center" as const,

            borderRadius: "20px",

            background:
                "linear-gradient(145deg, rgba(17,25,39,.96), rgba(8,13,22,.98))",

            border:
                `1px solid ${theme.border}`,

            boxShadow: theme.shadow,
        },

        resultIcon: {
            width: "70px",

            height: "70px",

            margin: "0 auto 20px",

            borderRadius: "18px",

            display: "flex",

            alignItems: "center",

            justifyContent: "center",

            fontSize: "30px",

            background:
                "rgba(96,165,250,.08)",

            border:
                "1px solid rgba(96,165,250,.18)",

            color: "#93c5fd",
        },

        resultTitle: {
            margin: 0,

            fontSize: "26px",

            fontWeight: 750,

            color: "#f8fafc",
        },

        resultSubtitle: {
            margin:
                "9px 0 26px",

            fontSize: "12px",

            color: "#64748b",
        },

        resultGrid: {
            display: "grid",

            gridTemplateColumns:
                "1fr 1fr",

            gap: "12px",
        },

        resultItem: {
            padding: "18px",

            borderRadius: "10px",

            background:
                "rgba(15,23,36,.70)",

            border:
                "1px solid rgba(148,163,184,.10)",
        },

        resultLabel: {
            margin: 0,

            fontSize: "9px",

            letterSpacing: "1px",

            textTransform:
                "uppercase" as const,

            color: "#64748b",
        },

        resultValue: {
            margin:
                "7px 0 0",

            fontSize: "21px",

            fontWeight: 750,

            color: "#e2e8f0",
        },
    };

    /* =========================================================
       FETCH COURSES
    ========================================================= */

    useEffect(() => {
        const fetchCourses = async () => {
            try {
                setLoading(true);

                const response =
                    await GetCoursesForExam({
                        courseTestEnum: 1,
                    });

                if (response.statusCode === 200) {
                    setCourses(
                        response.data.course
                    );
                }
            } catch (error) {
                console.error(
                    "Error fetching courses:",
                    error
                );
            } finally {
                setLoading(false);
            }
        };

        fetchCourses();
    }, []);

    /* =========================================================
       ATTEMPT RULES
    ========================================================= */

    const timingToStay = (
        attemptCount: number
    ): number => {
        switch (attemptCount) {
            case 1:
                return (
                    24 *
                    60 *
                    60 *
                    1000
                );

            case 2:
                return (
                    48 *
                    60 *
                    60 *
                    1000
                );

            case 3:
                return (
                    72 *
                    60 *
                    60 *
                    1000
                );

            default:
                return 0;
        }
    };

    const canAttemptCourse = (
        course: Course
    ) => {
        if (course.isPassed)
            return false;

        if (course.attemptCount >= 3)
            return false;

        if (!course.attemptTime)
            return true;

        const elapsed =
            new Date().getTime() -
            new Date(
                course.attemptTime
            ).getTime();

        return (
            elapsed >=
            timingToStay(
                course.attemptCount
            )
        );
    };

    const getCourseStatus = (
        course: Course
    ) => {
        if (course.isPassed)
            return "Passed";

        if (course.attemptCount >= 3)
            return "Attempts Exhausted";

        if (
            canAttemptCourse(
                course
            )
        ) {
            const remaining =
                3 -
                course.attemptCount;

            return `${remaining} ${
                remaining === 1
                    ? "Attempt"
                    : "Attempts"
            } Available`;
        }

        if (course.attemptCount === 1)
            return "Locked • 24 Hours";

        if (course.attemptCount === 2)
            return "Locked • 48 Hours";

        if (course.attemptCount === 3)
            return "Locked • 72 Hours";

        return "Not Available";
    };

    /* =========================================================
       START EXAM
    ========================================================= */

    const handleStart = async () => {
        if (
            !selectedCourseId ||
            !selectedCourse
        ) {
            alert(
                "Please select a course before starting the examination."
            );

            return;
        }

        if (
            !selectedCourse.examEligible
        ) {
            alert(
                "You are not currently eligible for this examination."
            );

            return;
        }

        if (
            !canAttemptCourse(
                selectedCourse
            )
        ) {
            alert(
                "This examination is currently locked for your account."
            );

            return;
        }

        try {
            setLoading(true);

            const response =
                await GetCoursesForExam({
                    courseId:
                        selectedCourseId,

                    courseTestEnum: 2,

                    totalQuestions: 0,

                    correctAnswers: 0,
                });

            if (
                response.statusCode === 200
            ) {
                const shuffled =
                    shuffleArray(
                        response.data
                            .courseTest
                    ) as CourseTest[];

                setQuestions(
                    shuffled
                );

                setCurrentIndex(0);

                setAnswers({});

                setTimer(
                    QUESTION_TIME
                );

                setSubmitted(false);

                setStartExam(true);
            } else {
                alert(
                    "Unable to initialize the examination."
                );
            }
        } catch (error) {
            console.error(
                "Error starting exam:",
                error
            );

            alert(
                "Unable to initialize the examination."
            );
        } finally {
            setLoading(false);
        }
    };

    /* =========================================================
       TIMER
    ========================================================= */

    useEffect(() => {
        if (
            !startExam ||
            submitted ||
            questions.length === 0
        ) {
            return;
        }

        const interval =
            setInterval(() => {
                setTimer(
                    (previous) => {
                        if (
                            previous <= 1
                        ) {
                            handleNext();

                            return QUESTION_TIME;
                        }

                        return (
                            previous - 1
                        );
                    }
                );
            }, 1000);

        return () =>
            clearInterval(
                interval
            );
    }, [
        startExam,
        submitted,
        currentIndex,
        questions.length,
    ]);

    /* =========================================================
       ANSWER
    ========================================================= */

    const handleOptionSelect = (
        option: number
    ) => {
        const questionId =
            questions[
                currentIndex
            ].courseTestId;

        setAnswers(
            (previous) => ({
                ...previous,

                [questionId]:
                    option,
            })
        );
    };

    /* =========================================================
       NEXT
    ========================================================= */

    const handleNext = () => {
        if (transitioning)
            return;

        setTransitioning(true);

        setTimeout(() => {
            setTransitioning(false);

            if (
                currentIndex <
                questions.length - 1
            ) {
                setCurrentIndex(
                    (previous) =>
                        previous + 1
                );

                setTimer(
                    QUESTION_TIME
                );
            } else {
                handleSubmit();
            }
        }, 250);
    };

    /* =========================================================
       SUBMIT
    ========================================================= */

    const handleSubmit =
        async () => {
            let correct = 0;

            const courseId =
                questions.length >
                0
                    ? questions[0]
                          .courseId
                    : selectedCourseId;

            questions.forEach(
                (question) => {
                    if (
                        answers[
                            question
                                .courseTestId
                        ] ===
                        question.answerOption
                    ) {
                        correct++;
                    }
                }
            );

            try {
                await GetCoursesForExam(
                    {
                        courseId:
                            courseId,

                        courseTestEnum: 3,

                        totalQuestions:
                            questions.length,

                        correctAnswers:
                            correct,
                    }
                );
            } catch (error) {
                console.error(
                    "Error submitting exam:",
                    error
                );
            }

            setScore(correct);

            setSubmitted(true);

            setStartExam(false);
        };

    /* =========================================================
       REFRESH PROTECTION
    ========================================================= */

    useEffect(() => {
        if (!startExam)
            return;

        const handleBeforeUnload =
            (
                event: BeforeUnloadEvent
            ) => {
                event.preventDefault();

                event.returnValue =
                    "Examination is currently in progress.";
            };

        window.addEventListener(
            "beforeunload",
            handleBeforeUnload
        );

        return () =>
            window.removeEventListener(
                "beforeunload",
                handleBeforeUnload
            );
    }, [startExam]);

    /* =========================================================
       EXAM SECURITY
    ========================================================= */

    useEffect(() => {
        if (!startExam)
            return;

        const blockContextMenu =
            (
                event: MouseEvent
            ) => {
                event.preventDefault();
            };

        const blockKeys =
            (
                event: KeyboardEvent
            ) => {
                const key =
                    event.key.toLowerCase();

                if (
                    event.ctrlKey &&
                    [
                        "c",
                        "p",
                        "s",
                        "u",
                    ].includes(key)
                ) {
                    event.preventDefault();

                    alert(
                        "This action is disabled during the examination."
                    );
                }

                if (
                    key ===
                    "printscreen"
                ) {
                    event.preventDefault();

                    alert(
                        "Screenshots are disabled during the examination."
                    );
                }

                if (
                    event.key ===
                    "F12"
                ) {
                    event.preventDefault();
                }

                if (
                    event.ctrlKey &&
                    event.shiftKey &&
                    [
                        "i",
                        "j",
                    ].includes(key)
                ) {
                    event.preventDefault();
                }
            };

        document.addEventListener(
            "contextmenu",
            blockContextMenu
        );

        document.addEventListener(
            "keydown",
            blockKeys
        );

        return () => {
            document.removeEventListener(
                "contextmenu",
                blockContextMenu
            );

            document.removeEventListener(
                "keydown",
                blockKeys
            );
        };
    }, [startExam]);

    /* =========================================================
       LOADING
    ========================================================= */

    if (loading) {
        return (
            <div
                style={{
                    ...styles.page,
                    display: "flex",
                    alignItems:
                        "center",
                    justifyContent:
                        "center",
                }}
            >
                <div
                    style={{
                        textAlign:
                            "center",
                    }}
                >
                    <div
                        style={{
                            width: "38px",
                            height: "38px",
                            border:
                                "3px solid rgba(96,165,250,.15)",
                            borderTop:
                                "3px solid #60a5fa",
                            borderRadius:
                                "50%",
                            margin:
                                "0 auto 15px",
                            animation:
                                "spin 1s linear infinite",
                        }}
                    />

                    <div
                        style={{
                            fontSize:
                                "13px",
                            fontWeight:
                                600,
                            color:
                                "#cbd5e1",
                        }}
                    >
                        Preparing secure examination...
                    </div>

                    <style>
                        {`
                            @keyframes spin {
                                from {
                                    transform: rotate(0deg);
                                }
                                to {
                                    transform: rotate(360deg);
                                }
                            }
                        `}
                    </style>
                </div>
            </div>
        );
    }

    /* =========================================================
       RESULT
    ========================================================= */

    if (submitted) {
        const percentage =
            questions.length > 0
                ? (score /
                      questions.length) *
                  100
                : 0;

        const passed =
            percentage >=
            PASSING_PERCENTAGE;

        return (
            <div style={styles.page}>
                <div
                    style={
                        styles.resultWrapper
                    }
                >
                    <div
                        style={
                            styles.resultCard
                        }
                    >
                        <div
                            style={{
                                ...styles.resultIcon,
                                color: passed
                                    ? "#67e8f9"
                                    : "#fb7185",
                                borderColor:
                                    passed
                                        ? "rgba(103,232,249,.20)"
                                        : "rgba(251,113,133,.20)",
                            }}
                        >
                            {passed
                                ? "✓"
                                : "!"}
                        </div>

                        <h1
                            style={
                                styles.resultTitle
                            }
                        >
                            Examination
                            Complete
                        </h1>

                        <p
                            style={
                                styles.resultSubtitle
                            }
                        >
                            Your responses have
                            been securely submitted.
                        </p>

                        <div
                            style={
                                styles.resultGrid
                            }
                        >
                            <div
                                style={
                                    styles.resultItem
                                }
                            >
                                <p
                                    style={
                                        styles.resultLabel
                                    }
                                >
                                    Score
                                </p>

                                <p
                                    style={
                                        styles.resultValue
                                    }
                                >
                                    {score} /{" "}
                                    {
                                        questions.length
                                    }
                                </p>
                            </div>

                            <div
                                style={
                                    styles.resultItem
                                }
                            >
                                <p
                                    style={
                                        styles.resultLabel
                                    }
                                >
                                    Accuracy
                                </p>

                                <p
                                    style={
                                        styles.resultValue
                                    }
                                >
                                    {percentage.toFixed(
                                        2
                                    )}
                                    %
                                </p>
                            </div>
                        </div>

                        <div
                            style={{
                                marginTop:
                                    "14px",

                                padding:
                                    "14px",

                                borderRadius:
                                    "9px",

                                background:
                                    passed
                                        ? "rgba(34,211,238,.05)"
                                        : "rgba(251,113,133,.05)",

                                border:
                                    passed
                                        ? "1px solid rgba(34,211,238,.13)"
                                        : "1px solid rgba(251,113,133,.13)",

                                color:
                                    passed
                                        ? "#67e8f9"
                                        : "#fb7185",

                                fontSize:
                                    "12px",

                                fontWeight:
                                    700,
                            }}
                        >
                            {passed
                                ? "EXAMINATION PASSED"
                                : "EXAMINATION NOT PASSED"}
                        </div>

                        <div
                            style={{
                                marginTop:
                                    "15px",

                                color:
                                    "#475569",

                                fontSize:
                                    "9px",

                                letterSpacing:
                                    ".6px",

                                textTransform:
                                    "uppercase",
                            }}
                        >
                            Passing requirement{" "}
                            {PASSING_PERCENTAGE}%
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    /* =========================================================
       COURSE SELECTION
    ========================================================= */

    if (!startExam) {
        return (
            <div style={styles.page}>
                <div
                    style={
                        styles.grid
                    }
                />

                <div
                    style={
                        styles.selectionWrapper
                    }
                >
                    <div
                        style={
                            styles.selectionCard
                        }
                    >
                        <div
                            style={
                                styles.selectionHeader
                            }
                        >
                            <div
                                style={
                                    styles.securityLine
                                }
                            >
                                <span
                                    style={
                                        styles.securityDot
                                    }
                                />

                                <span
                                    style={
                                        styles.securityText
                                    }
                                >
                                    Secure Assessment Environment
                                </span>
                            </div>

                            <h1
                                style={
                                    styles.selectionTitle
                                }
                            >
                                Examination
                                Center
                            </h1>

                            <p
                                style={
                                    styles.selectionSubtitle
                                }
                            >
                                Select your eligible
                                examination below.
                                Once started, the
                                examination session
                                will be secured.
                            </p>
                        </div>

                        <div
                            style={
                                styles.selectionBody
                            }
                        >
                            <label
                                style={
                                    styles.label
                                }
                            >
                                Available
                                Examination
                            </label>

                            <select
                                style={
                                    styles.select
                                }
                                value={
                                    selectedCourseId ??
                                    ""
                                }
                                onChange={(
                                    event
                                ) => {
                                    const id =
                                        Number(
                                            event
                                                .target
                                                .value
                                        );

                                    const course =
                                        courses.find(
                                            (
                                                item
                                            ) =>
                                                item.courseId ===
                                                id
                                        );

                                    setSelectedCourseId(
                                        id
                                    );

                                    setSelectedCourse(
                                        course
                                    );
                                }}
                            >
                                <option
                                    value=""
                                    disabled
                                >
                                    Select an examination
                                </option>

                                {courses.map(
                                    (
                                        course
                                    ) => (
                                        <option
                                            key={
                                                course.courseId
                                            }
                                            value={
                                                course.courseId
                                            }
                                            disabled={
                                                !canAttemptCourse(
                                                    course
                                                ) ||
                                                !course.examEligible
                                            }
                                        >
                                            {
                                                course.courseName
                                            }{" "}
                                            •{" "}
                                            {getCourseStatus(
                                                course
                                            )}
                                        </option>
                                    )
                                )}
                            </select>

                            {selectedCourse && (
                                <div
                                    style={
                                        styles.infoPanel
                                    }
                                >
                                    <p
                                        style={
                                            styles.infoTitle
                                        }
                                    >
                                        Examination
                                        Details
                                    </p>

                                    <div
                                        style={
                                            styles.infoRow
                                        }
                                    >
                                        <span
                                            style={
                                                styles.infoLabel
                                            }
                                        >
                                            Examination
                                        </span>

                                        <span
                                            style={
                                                styles.infoValue
                                            }
                                        >
                                            {
                                                selectedCourse.courseName
                                            }
                                        </span>
                                    </div>

                                    <div
                                        style={
                                            styles.infoRow
                                        }
                                    >
                                        <span
                                            style={
                                                styles.infoLabel
                                            }
                                        >
                                            Attempts
                                        </span>

                                        <span
                                            style={
                                                styles.infoValue
                                            }
                                        >
                                            {
                                                selectedCourse.attemptCount
                                            }{" "}
                                            / 3
                                        </span>
                                    </div>

                                    <div
                                        style={
                                            styles.infoRow
                                        }
                                    >
                                        <span
                                            style={
                                                styles.infoLabel
                                            }
                                        >
                                            Status
                                        </span>

                                        <span
                                            style={{
                                                ...styles.infoValue,
                                                color:
                                                    canAttemptCourse(
                                                        selectedCourse
                                                    )
                                                        ? "#67e8f9"
                                                        : "#94a3b8",
                                            }}
                                        >
                                            {getCourseStatus(
                                                selectedCourse
                                            )}
                                        </span>
                                    </div>

                                    <div
                                        style={
                                            styles.infoRow
                                        }
                                    >
                                        <span
                                            style={
                                                styles.infoLabel
                                            }
                                        >
                                            Passing Score
                                        </span>

                                        <span
                                            style={
                                                styles.infoValue
                                            }
                                        >
                                            {
                                                PASSING_PERCENTAGE
                                            }
                                            %
                                        </span>
                                    </div>

                                    <div
                                        style={{
                                            ...styles.infoRow,
                                            borderBottom:
                                                "none",
                                        }}
                                    >
                                        <span
                                            style={
                                                styles.infoLabel
                                            }
                                        >
                                            Question Timer
                                        </span>

                                        <span
                                            style={
                                                styles.infoValue
                                            }
                                        >
                                            {
                                                QUESTION_TIME
                                            }{" "}
                                            seconds
                                        </span>
                                    </div>
                                </div>
                            )}

                            <button
                                type="button"
                                onClick={
                                    handleStart
                                }
                                disabled={
                                    !selectedCourseId
                                }
                                style={{
                                    ...styles.startButton,
                                    opacity:
                                        !selectedCourseId
                                            ? 0.45
                                            : 1,
                                    cursor:
                                        !selectedCourseId
                                            ? "not-allowed"
                                            : "pointer",
                                }}
                            >
                                Begin Secure Examination
                            </button>

                            <div
                                style={{
                                    marginTop:
                                        "18px",

                                    display:
                                        "flex",

                                    alignItems:
                                        "center",

                                    justifyContent:
                                        "center",

                                    gap: "7px",

                                    color:
                                        "#475569",

                                    fontSize:
                                        "9px",

                                    letterSpacing:
                                        ".4px",
                                }}
                            >
                                <span>
                                    🔒
                                </span>

                                Secure examination
                                session
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    /* =========================================================
       NO QUESTIONS
    ========================================================= */

    if (
        questions.length === 0
    ) {
        return (
            <div
                style={{
                    ...styles.page,
                    display:
                        "flex",
                    alignItems:
                        "center",
                    justifyContent:
                        "center",
                }}
            >
                <div
                    style={{
                        color:
                            "#94a3b8",
                        fontSize:
                            "13px",
                    }}
                >
                    Initializing secure
                    examination...
                </div>
            </div>
        );
    }

    /* =========================================================
       CURRENT QUESTION
    ========================================================= */

    const currentQuestion =
        questions[currentIndex];

    const selectedAnswer =
        answers[
            currentQuestion
                .courseTestId
        ];

    const progress =
        ((currentIndex + 1) /
            questions.length) *
        100;

    const options = [
        currentQuestion.option1,
        currentQuestion.option2,
        currentQuestion.option3,
        currentQuestion.option4,
    ];

    /* =========================================================
       EXAM SCREEN
    ========================================================= */

    return (
        <div style={styles.page}>
            <div
                style={styles.grid}
            />

            <div
                style={
                    styles.examWrapper
                }
            >
                <div
                    style={
                        styles.examContainer
                    }
                >
                    {/* EXAM HEADER */}

                    <div
                        style={
                            styles.examHeader
                        }
                    >
                        <div
                            style={
                                styles.examBrand
                            }
                        >
                            <div
                                style={
                                    styles.brandIcon
                                }
                            >
                                ⬡
                            </div>

                            <div
                                style={{
                                    minWidth:
                                        0,
                                }}
                            >
                                <h2
                                    style={
                                        styles.examName
                                    }
                                >
                                    {
                                        selectedCourse?.courseName
                                    }
                                </h2>

                                <p
                                    style={
                                        styles.examStatus
                                    }
                                >
                                    Examination
                                    session active
                                </p>
                            </div>
                        </div>

                        <div
                            style={
                                styles.secureBadge
                            }
                        >
                            <span>
                                ●
                            </span>

                            SECURE MODE
                        </div>

                        <div
                            style={{
                                ...styles.timer,
                                color:
                                    timer <=
                                    5
                                        ? "#fb7185"
                                        : "#e2e8f0",

                                borderColor:
                                    timer <=
                                    5
                                        ? "rgba(251,113,133,.30)"
                                        : "rgba(148,163,184,.15)",

                                boxShadow:
                                    timer <=
                                    5
                                        ? "0 0 20px rgba(251,113,133,.10)"
                                        : "none",
                            }}
                        >
                            {timer}s
                        </div>
                    </div>

                    {/* PROGRESS */}

                    <div
                        style={
                            styles.progressPanel
                        }
                    >
                        <div
                            style={
                                styles.progressHeader
                            }
                        >
                            <span
                                style={
                                    styles.progressText
                                }
                            >
                                QUESTION{" "}
                                {String(
                                    currentIndex +
                                        1
                                ).padStart(
                                    2,
                                    "0"
                                )}{" "}
                                /{" "}
                                {String(
                                    questions.length
                                ).padStart(
                                    2,
                                    "0"
                                )}
                            </span>

                            <span
                                style={
                                    styles.progressText
                                }
                            >
                                {Math.round(
                                    progress
                                )}
                                %
                            </span>
                        </div>

                        <div
                            style={
                                styles.progressTrack
                            }
                        >
                            <div
                                style={{
                                    height:
                                        "100%",

                                    width: `${progress}%`,

                                    background:
                                        "linear-gradient(90deg, #2563eb, #60a5fa)",

                                    boxShadow:
                                        "0 0 15px rgba(96,165,250,.30)",

                                    transition:
                                        "width .3s ease",
                                }}
                            />
                        </div>
                    </div>

                    {/* QUESTION CARD */}

                    <div
                        style={{
                            ...styles.questionCard,

                            opacity:
                                transitioning
                                    ? 0.55
                                    : 1,

                            transform:
                                transitioning
                                    ? "translateX(-8px)"
                                    : "translateX(0)",
                        }}
                    >
                        <div
                            style={
                                styles.questionLabel
                            }
                        >
                            QUESTION{" "}
                            {currentIndex +
                                1}
                        </div>

                        <h3
                            style={
                                styles.questionText
                            }
                        >
                            {
                                currentQuestion.courseQuestion
                            }
                        </h3>

                        <div
                            style={
                                styles.options
                            }
                        >
                            {options.map(
                                (
                                    option,
                                    index
                                ) => {
                                    const optionNumber =
                                        index +
                                        1;

                                    const selected =
                                        selectedAnswer ===
                                        optionNumber;

                                    return (
                                        <label
                                            key={
                                                index
                                            }
                                            style={{
                                                ...styles.option,

                                                ...(selected
                                                    ? styles.selectedOption
                                                    : {}),
                                            }}
                                        >
                                            <input
                                                type="radio"
                                                name={`question-${currentQuestion.courseTestId}`}
                                                value={
                                                    optionNumber
                                                }
                                                checked={
                                                    selected
                                                }
                                                onChange={() =>
                                                    handleOptionSelect(
                                                        optionNumber
                                                    )
                                                }
                                                style={
                                                    styles.radio
                                                }
                                            />

                                            <span
                                                style={{
                                                    ...styles.optionText,

                                                    color:
                                                        selected
                                                            ? "#e2e8f0"
                                                            : "#94a3b8",

                                                    fontWeight:
                                                        selected
                                                            ? 600
                                                            : 500,
                                                }}
                                            >
                                                {
                                                    option
                                                }
                                            </span>
                                        </label>
                                    );
                                }
                            )}
                        </div>

                        <button
                            type="button"
                            onClick={
                                handleNext
                            }
                            disabled={
                                transitioning
                            }
                            style={{
                                ...styles.nextButton,

                                opacity:
                                    transitioning
                                        ? 0.5
                                        : 1,
                            }}
                        >
                            {currentIndex ===
                            questions.length -
                                1
                                ? "Submit Examination"
                                : "Save Answer & Continue"}
                        </button>

                        <div
                            style={
                                styles.examFooter
                            }
                        >
                            <span>
                                Answers are
                                automatically
                                recorded
                            </span>

                            <span>
                                🔒 Session
                                protected
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CourseTestSlider;